// Bundled English translations — static import, zero network cost.
// This is the source of truth. All other language files must follow this structure.
export const en = {
    common: {
        loading: 'Loading...',
        save: 'Save',
        saving: 'Saving…',
        cancel: 'Cancel',
        submit: 'Submit',
        close: 'Close',
        back: 'Back',
        tryAgain: 'Try again',
        create: 'Create',
        creating: 'Creating…',
        error: 'An error occurred',
        success: 'Success',
        view: 'View',
        manage: 'Manage',
        upload: 'Upload',
        download: 'Download',
        delete: 'Delete',
        refresh: 'Refresh',
        addAnother: 'Add another',
        viewMore: 'View more',
        selectDates: 'Select dates',
        applyFilter: 'Apply filter',
        loadMore: 'Load more',
    },

    meta: {
        title: 'Nolia',
        description: 'Nolia AI funding platform',
    },

    images: {
        noliaLogo: 'Nolia',
        placeholderAvatar: 'User avatar',
        bannerImage: 'Banner image',
    },

    nav: {
        dashboard: 'Dashboard',
        knowledgeBases: 'Knowledge Bases',
        createDocuments: 'Create Documents',
        procure: 'Procure',
        validateDocuments: 'Validate Documents',
        assessApplications: 'Assess Applications',
        applicationSetup: 'Application Setup',
        analytics: 'Analytics',
        team: 'User Management',
        profile: 'Profile',
        documents: 'User Guide',
        signOut: 'Sign out',
        searchPlaceholder: 'Search',
    },

    errors: {
        notFound: {
            code: '404 error',
            title: "We can't find that page",
            description: "Sorry, the page you are looking for doesn't exist or has been moved.",
            backButton: 'Go back',
            homeButton: 'Take me home',
        },
    },

    dashboard: {
        greetingMorning: 'Good morning',
        greetingAfternoon: 'Good afternoon',
        greetingEvening: 'Good evening',
        tagline: 'Nolia massively minimises the time it takes you to create and assess key procurement documents from months to minutes.',
        analyticsTitle: 'Analytics',
        analyticsViewMore: 'View more',
        analyticsSelectDates: 'Select dates',
        analyticsApplyFilter: 'Apply filter',
        analyticsDocsValidated: 'Documents Validated',
        analyticsKbCreated: 'Knowledge Bases',
        analyticsResponsesEvaluated: 'Responses Evaluated',
        kbGlobal: 'Global KBs',
        kbProject: 'Project KBs',
        kbProcurement: 'Procurement Activity KBs',
        kbView: 'View',
        kbCreate: 'Create',
        kbVsLastMonth: '% vs last month',
        usersTitle: 'Users',
        usersSubtitle: 'Add or manage users',
        usersActive: 'Active users',
        usersAdd: 'Add a user',
        usersManage: 'Manage',
        usageTitle: 'Usage',
        usageAlmostLimit: "You've almost reached your limit",
        usageText: 'You have used {percent}% of your monthly data.',
        usageContactUs: 'Contact us to increase.',
        featureCreateTitle: 'Create Documents',
        featureCreateDescription: 'The ability to create documents from scratch (e.g. ToRs, RFBs, etc) using all the right templates and guidelines.',
        featureCreateAction: 'Create',
        featureValidateTitle: 'Validate Documents',
        featureValidateDescription: 'The ability to validate important documentation (e.g. TER, CER, ToR) before sending to MDB.',
        featureValidateAction: 'Validate',
        featureEvaluateTitle: 'Evaluate Responses',
        featureEvaluateDescription: 'The ability to assess responses to procurement activities at speed and compare submitters.',
        featureEvaluateAction: 'Evaluate',
        fundingGreeting: 'Kia ora',
        fundingTagline: 'Nolia processes high-volume funding rounds at speed — consistent assessment, quality decisions, no back-and-forth.',
        kbFunding: 'Funding KBs',
        comingSoon: 'Coming soon',
        analyticsComingSoonSubtitle: 'We just need a bit more data before we can show you relevant analytics',
    },

    analytics: {
        title: 'Analytics',
        comingSoon: 'Detailed analytics are coming soon.',
    },

    auth: {
        loginPageTitle: 'Nolia | Login',
        loginLoadingText: 'Loading login...',
        loginHeading: 'Log in to your account',
        loginSubtitle: 'Welcome back! Please enter your details.',
        loginEmailLabel: 'Email',
        loginEmailPlaceholder: 'Enter your email address',
        loginPasswordLabel: 'Password',
        loginPasswordPlaceholder: '••••••••',
        loginForgotPassword: 'Forgot password?',
        loginSignInButton: 'Sign in',
        loginSigningInButton: 'Signing in...',
        loginPasswordUpdatingButton: 'Updating password...',
        loginSetNewPasswordButton: 'Set new password',
        loginPreparingButton: 'Preparing...',
        loginErrorEmptyFields: 'Please enter both email and password',
        loginErrorSessionNotReady: 'Still preparing secure session, please wait a moment.',
        loginAuthenticatingMessage: 'Authenticating...',
        loginSuccessMessage: 'Login successful! Redirecting...',
        loginRequiresPasswordMessage: 'You must set a new password to continue.',
        loginErrorDefault: 'Login failed. Please check your credentials.',
        loginNewPasswordLabel: 'New Password',
        loginNewPasswordPlaceholder: 'Enter a new password',
        loginConfirmPasswordLabel: 'Confirm Password',
        loginConfirmPasswordPlaceholder: 'Re-enter new password',
        loginErrorPasswordMismatch: "Passwords don't match",
        loginErrorPasswordSetFailed: 'Failed to set new password. Please try again.',
        forgotStep1Heading: 'Forgot your password?',
        forgotStep1Subtitle: "No worries, we'll send you reset instructions.",
        forgotStep1EmailLabel: 'Email',
        forgotStep1EmailPlaceholder: 'Enter your email',
        forgotStep1SubmitButton: 'Reset password',
        forgotStep1BackToLogin: 'Back to log in',
        forgotStep1ErrorEmptyEmail: 'Please enter your email address.',
        forgotStep1ErrorServiceNotConfigured: 'Authentication service is not configured.',
        forgotStep1ErrorSendFailed: 'Unable to send reset email.',
        forgotStep2Heading: 'Check your email',
        forgotStep2Subtitle: 'We sent a password reset code to',
        forgotStep2SubtitleDefault: 'your email address',
        forgotStep2OpenEmailButton: 'Open email app',
        forgotStep2ResendPrompt: "Didn't receive the email?",
        forgotStep2ResendButton: 'Click to resend',
        forgotStep2CodePrompt: 'Enter reset code',
        forgotStep2BackToLogin: 'Back to log in',
        forgotStep2ErrorMissingEmail: 'Missing email address. Please start the reset flow again.',
        forgotStep2ErrorResendFailed: 'Unable to resend reset email.',
        forgotStep2SuccessMessage: 'Reset email sent. Check your inbox for the verification code.',
        forgotStep3Heading: 'Set new password',
        forgotStep3Subtitle: 'Your new password must be different to previously used passwords.',
        forgotStep3VerificationCodeLabel: 'Verification code',
        forgotStep3VerificationCodePlaceholder: 'Enter the code from your email',
        forgotStep3PasswordLabel: 'Password',
        forgotStep3PasswordPlaceholder: '••••••••',
        forgotStep3ConfirmPasswordLabel: 'Confirm password',
        forgotStep3ConfirmPasswordPlaceholder: '••••••••',
        forgotStep3SubmitButton: 'Reset password',
        forgotStep3BackToLogin: 'Back to log in',
        forgotStep3ErrorMissingEmail: 'Missing email address. Please restart the reset flow.',
        forgotStep3ErrorMissingCode: 'Please enter the verification code from your email.',
        forgotStep3ErrorEmptyPassword: 'Please enter and confirm your new password.',
        forgotStep3ErrorPasswordMismatch: 'Passwords do not match.',
        forgotStep3ErrorResetFailed: 'Unable to reset password.',
        forgotStep3Requirement8Chars: 'Must be at least 8 characters',
        forgotStep3RequirementSpecialChar: 'Must contain one special character',
        forgotStep4Heading: 'Password reset',
        forgotStep4Subtitle: 'Your password has been successfully reset. Click below to log in magically.',
        forgotStep4ContinueButton: 'Continue',
        forgotStep4BackToLogin: 'Back to log in',
        signupPageTitle: 'Nolia | Create Password',
        signupHeading: 'Create your password',
        signupSubtitle: 'To continue to Nolia',
        signupEmailLabel: 'Email',
        signupEmailPlaceholder: 'Enter your email',
        signupActivationCodeLabel: 'Activation code',
        signupActivationCodePlaceholder: 'Enter your activation code',
        signupPasswordLabel: 'Password',
        signupPasswordPlaceholder: 'Create a password',
        signupConfirmPasswordLabel: 'Confirm password',
        signupConfirmPasswordPlaceholder: 'Confirm your password',
        signupRequirement8Chars: 'Must be at least 8 characters',
        signupRequirementSpecialChar: 'Must contain one special character',
        signupCreatePasswordButton: 'Create password',
        signupHaveAccountPrompt: 'Already have an account?',
        signupLoginLink: 'Log in',
        emailVerifyPageTitle: 'Verify Your Email',
        emailVerifyPageDescription: 'Enter the 6-digit verification code sent to your email address',
        emailVerifyBackToLogin: 'Back to Login',
        emailVerifyEmailLabel: 'Email Address',
        emailVerifyEmailPlaceholder: 'Enter your email address',
        emailVerifyCodeLabel: 'Verification Code',
        emailVerifyCodePlaceholder: '123456',
        emailVerifyCodeHint: 'Enter the 6-digit code from your email',
        emailVerifyButton: 'Verify Email',
        emailVerifyingButton: 'Verifying...',
        emailVerifySuccessTitle: 'Email Verified Successfully!',
        emailVerifySuccessDescription: "Your email has been verified. You'll be redirected to the login page in a few seconds.",
        emailVerifyContinueButton: 'Continue to Login',
        emailVerifyDidntReceive: "Didn't receive the code?",
        emailVerifyResendIn: 'Resend available in {seconds} seconds',
        emailVerifyResendButton: 'Resend Code',
        emailVerifySendingButton: 'Sending...',
        emailVerifyHelpText: "Check your spam folder if you don't see the email. The verification code expires in 10 minutes.",
        emailVerifyErrorEmailRequired: 'Please enter your email address first',
        emailVerifyErrorInvalidEmail: 'Please enter a valid email address',
        emailVerifyErrorFailed: 'Verification failed. Please check your code and try again.',
    },

    team: {
        pageTitle: 'User Management',
        pageDescription: 'Manage your team members and their account permissions here.',
        inviteTitle: 'Invite team members',
        inviteDescription: 'Get your projects up and running faster by inviting your team to collaborate.',
        emailPlaceholder: 'you@example.com',
        rolePlaceholder: 'Role',
        addAnother: 'Add another',
        sendInvites: 'Send invites',
        teamMembersTitle: 'Team members',
        teamMembersDescription: 'Manage your existing team and change roles/permissions.',
        tableHeaderName: 'Name',
        tableHeaderCategory: 'Procurement Activities',
        tableHeaderRole: 'Role',
        tableHeaderStatus: 'Status',
        statusActive: 'Active',
        statusPending: 'Pending',
        statusSuspended: 'Suspended',
        statusInactive: 'Deactivated',
        tooltipViewDetails: 'View details',
        tooltipRemoveMember: 'Remove member',
        confirmRemove: 'Are you sure you want to remove {email} from the team?',
        successMessage: '{count} invitation(s) sent successfully!',
        errorLoadingTeam: 'Failed to load team data. Please refresh the page.',
        errorRemovingMember: 'Failed to remove team member. Please try again.',
        errorSendingInvites: '{count} invitation(s) failed to send.',
        errorSendingInvitesGeneral: 'Failed to send invitations. Please try again.',
        removeSuccess: '{email} has been removed from the team',
        tooltipResendCode: 'Resend activation code',
        resendSuccess: 'Activation code resent to {email}',
        errorResendingCode: 'Failed to resend activation code. Please try again.',
        assignProcurementActivity: 'Assign Procurement Activity',
        procurementActivitiesCount: '{count} activities',
        procurementActivitiesPopoverTitle: 'Procurement Activities',
        procurementActivitiesPopoverDesc: 'Which Procurement Activities do you want them to see',
        procurementActivitiesAssignedDesc: 'The Procurement Activities assigned to this user',
        procurementActivitiesLabel: 'Procurement Activities',
        procurementActivitiesDescription: 'Assign which Procurement Activities to see',
        editActivities: 'Edit',
        noActivitiesAssigned: 'No activities assigned',
        activityCountSingular: '{count} activity',
        activityCountPlural: '{count} activities',
        invalidEmailAddress: 'Invalid email address: {emails}',
        failedToInvite: 'Failed to invite: {details}',
        roleAdmin: 'Admin',
        roleOperator: 'Operator',
        detailLabelName: 'Name',
        detailDescName: 'Please use your full name \u2014 first and last names.',
        detailLabelEmail: 'Email address',
        detailDescEmail: 'Your account email.',
        detailLabelStatus: 'Status',
        detailDescStatus: 'Control whether this user can access the platform.',
        detailLabelRole: 'Role',
        detailDescRole: 'Controls what the user can do in the platform.',
        detailLabelPermissions: 'Permissions',
        detailDescPermissions: 'Current permissions assigned to this user.',
        detailLabelMemberSince: 'Member since',
        detailBackToTeam: 'Back',
        detailDeleteUser: 'Delete profile',
        detailConfirmDelete: 'Are you sure you want to delete {email}? This action cannot be undone.',
        detailSaveSuccess: 'User profile updated successfully.',
        detailSaveError: 'Failed to save changes',
        detailUserNotFound: 'User not found',
        detailLoadError: 'Failed to load user details',
    },

    userGuide: {
        subtitle: 'User documentation',
        heading: 'What do you need to know today?',
        placeholder: 'Ask a question about using Nolia...',
        thinking: 'Thinking...',
        connecting: 'Starting Nolia local assistant...',
        connectionFailed: 'Unable to connect to the assistant. Please try again later.',
    },

    profile: {
        pageTitle: 'Profile Settings',
        pageDescription: 'Update your photo and personal details here.',
        cancel: 'Cancel',
        save: 'Save',
        saving: 'Saving…',
        labelName: 'Name',
        labelNameRequired: 'Name *',
        descriptionName: 'Please use your full name \u2014 first and last names.',
        placeholderName: 'Your name',
        labelEmail: 'Email address',
        descriptionEmail: 'Your account email',
        placeholderEmail: 'email@example.com',
        labelStatus: 'Status',
        statusValue: 'Active',
        labelPassword: 'Password',
        descriptionPassword: 'Please enter your current password to change your password.',
        placeholderCurrentPassword: 'Current password',
        placeholderNewPassword: 'New password',
        placeholderConfirmPassword: 'Confirm new password',
        labelPhoto: 'Your photo',
        descriptionPhoto: 'This will be displayed on your profile',
        photoDropzoneText: 'Click to upload',
        photoDropzoneOr: ' or drag and drop',
        photoDropzoneHint: 'SVG, PNG, JPG or GIF (max. 800×400px)',
        photoPlaceholder: 'Profile photo',
        labelRole: 'Role',
        descriptionRole: 'Your account role and permissions',
        deleteAccountButton: 'Delete account',
        updatePhoto: 'Update photo',
        deletePhoto: 'Delete',
        sidebarActions: 'Actions',
        sidebarGoToDashboard: 'Go to Dashboard',
        sidebarManageTeam: 'Manage Team',
        errorImageFilesOnly: 'Only image files are allowed',
        errorFileSizeTooLarge: 'File size must be less than 5MB',
        errorPasswordRequired: 'New password is required',
        errorPasswordMismatch: 'Passwords do not match',
        errorPasswordMinLength: 'Password must be at least 8 characters',
        errorPasswordChangeFailed: 'Failed to change password. Please check your current password and try again.',
        successProfileSaved: 'Profile saved successfully',
        roleAdmin: 'Admin',
        roleUser: 'Operator',
        roleUnknown: 'Operator',
    },

    assessment: {
        uploadSubtitle: 'Assess',
        uploadProgressStep1: 'Upload document',
        uploadProgressDescription: 'Upload document to review',
        selectGlobalKBLabel: '1. Select the global knowledge base for evaluation',
        selectGlobalKBLabelToR: '1. Select the global knowledge base for ToR',
        selectGlobalKBPlaceholder: 'Select a global knowledge base',
        noGlobalKBAvailable: 'No global knowledge bases available',
        loadingKBs: 'Loading knowledge bases...',
        selectProcurementKBLabel: '2. Select the Fund to assess against',
        selectProjectKBLabel: '2. Select the relevant project knowledge base',
        selectProcurementKBPlaceholder: 'Select a funding knowledge base',
        noProcurementKBAvailable: 'No funding knowledge bases available',
        selectProjectKBPlaceholder: 'Select a project knowledge base',
        noProjectKBAvailable: 'No project knowledge bases available',
        selectOutputLanguage: '3. Select output language',
        selectOutputLanguagePlaceholder: 'Select output language',
        english: 'English',
        bahasaIndonesia: 'Bahasa Indonesia',
        uploadDocumentsLabel: '4. Upload document(s) to assess',
        fileUploadHint: 'PDF, Word, images, or HTML up to 50MB each',
        runAssessmentButton: 'Run assessment',
        startingButton: 'Starting...',
        errorSelectKBAndUpload: 'Select the required knowledge bases and upload at least one document before running an assessment.',
        errorAssessmentFailed: 'Assessment failed: {message}',
        historyTitle: 'Assessment Dashboard',
        historySubtitle: 'Assess',
        chartLabelMonth: 'Month',
        chartLabelAssessments: 'Assessments',
        chartLabelTrend: 'Trend',
        evaluationReport: 'Assess Evaluation Report',
        termsOfReference: 'Assess Terms of Reference',
        sidebarActionsTitle: 'Actions',
        sidebarAssessor: 'Assessor',
        jobDetailTitle: 'Assessment Results',
        jobStatusSuccess: 'SUCCESS',
        jobStatusProcessing: 'PROCESSING',
        jobStatusFailure: 'FAILURE',
        backToHistory: 'Back to History',
        refresh: 'Refresh',
        documentLabel: 'Document:',
        processingTitle: 'Assessment in Progress',
        processingDescription: 'Your document is being analyzed. This may take 30 minutes to 1.5 hours.',
        autoRefreshNote: 'Auto-refreshing every 10 seconds...',
        errorLoadingAssessment: 'Error Loading Assessment',
        failureTitle: 'Assessment Failed',
        failureDefaultError: 'An error occurred while processing your assessment.',
        tryAgainButton: 'Try Again',
        loadingMessage: 'Loading assessment...',

        // Document Overview (Page 1)
        docOverviewTitle: 'Document Overview',
        docOverviewSubtitle: 'Validate Documents',
        documentsChecked: 'Documents checked',
        uploadDocument: 'Upload Document',
        selectDates: 'Select dates',
        applyFilter: 'Apply filter',
        tableDocumentCol: 'Document',
        tableTypeCol: 'Type',
        tableAssessedOnCol: 'Assessed on',
        tableLanguageCol: 'Language',
        tableCategoryCol: 'Category',
        tableGlobalKBCol: 'Global KB',
        tableActivityKBCol: 'Activity KB',
        tableDurationCol: 'Duration',
        recentUploadActivity: 'Recent upload activity',
        noRecentActivity: 'No recent activity.',
        last7Days: 'Last 7 days',
        noUploadsRecent: 'No uploads in the last 7 days.',
        typeFundingApplication: 'Funding Application',
        viewDetails: 'View details',
        tableStatusCol: 'Status',
        tooltipView: 'View',
        tooltipDelete: 'Delete',
        loadingAssessments: 'Loading assessments...',
        emptyStateTitle: 'No assessments yet',
        emptyStateSubtitle: 'Upload a document to run your first assessment.',
        confirmDeleteAssessment: 'Are you sure you want to delete this assessment?',
        typeEvaluationReport: 'Evaluation Report',
        typeTermsOfReference: 'Terms of Reference',
        typeAssessment: 'Assessment',
        langBahasa: 'Bahasa',
        langEnglish: 'English',
        relativeJustNow: 'Just now',
        relativeMinutesAgo: '{minutes}m ago',
        relativeHoursAgo: '{hours}h ago',
        relativeDaysAgo: '{days}d ago',

        // Document Upload (Page 2)
        docUploadTitle: 'Document Upload',
        docUploadSubtitle: 'Validate Documents',
        stepUpload: 'Upload',
        stepUploadDesc: 'Upload documents to verify',
        stepVerification: 'Verification',
        stepVerificationDesc: 'Automated AI document checker',
        selectGlobalKBAssessLabel: 'Select the Global Knowledge Base to assess against',
        selectProcurementActivityLabel: 'Select the Fund to assess against',
        selectOutputLanguageLabel: 'Select the language you\'d like the output',
        uploadDocumentsToAssess: 'Upload document(s) to assess.',
        activeStatus: 'active',

        // Assessment Results (Page 3)
        evalReportTitle: 'Evaluation Report',
        terReportTitle: 'Terms of Reference',
        assessmentComplete: 'Assessment Complete',
        assessmentCompleteDesc: 'Nolia-powered Evaluation Report against Global and Procurement Activity Knowledge Bases has been completed.',
        completeBadge: 'Complete',
        downloadAsPdf: 'Download as PDF',
        statusComplete: 'Status: Complete',
        statusCompleteDesc: 'This assessment has been successfully completed and is ready for review.',
        aiPoweredAssessment: 'AI-Powered Assessment',
        aiPoweredAssessmentDesc: 'Evaluated against World Bank standards',
        complianceVerified: 'Compliance Verified',
        complianceVerifiedDesc: 'All documents reviewed for compliance requirements',
    },

    kb: {
        selectPrompt: 'Select a knowledge base',
        selectDescription: 'Use the {categoryLabel} page and choose "View knowledge base" on any card.',
        monitorStatus: 'Monitor knowledge base status and manage documents',
        statusTitle: 'Knowledge Base Complete',
        statusComplete: 'This knowledge base has been processed and is ready for use.',
        statusProcessing: 'Processing documents',
        statusFailedTitle: 'Knowledge Base Failed',
        statusFailedDefault: 'Rules generation did not complete. Use Reconfigure to retry, or delete this knowledge base.',
        detailsTitle: 'Knowledge Base Details',
        totalDocuments: 'Total Documents',
        status: 'Status',
        category: 'Category',
        complete: 'Complete',
        processingBadge: 'Processing',
        kbInformation: 'KB Information',
        kbId: 'ID:',
        description: 'Description:',
        role: 'Role:',
        uploadDocumentsTitle: 'Upload Documents',
        uploadDocumentsDescription: 'Add new documents to this knowledge base for processing and analysis.',
        targetFolderLabel: 'Target folder (optional)',
        targetFolderPlaceholder: 'e.g. finance/rfp/',
        targetFolderHint: 'Files will be uploaded to this path prefix',
        createNewFolderLabel: 'Create new folder',
        createNewFolderPlaceholder: 'e.g. proposals/2024',
        selectFilesLabel: 'Select files',
        selectFolderLabel: 'Or select folder',
        filesSelectedFromFolder: '{count} files from folder "{folderName}"',
        filesSelectedPlural: '{count} files selected',
        filesSelectedSingular: '1 file selected',
        uploadingProgress: 'Uploading {current} of {total}',
        uploadingSingle: 'Uploading file',
        uploadingCurrent: 'Current: {fileName}',
        uploadError: 'Upload Error',
        errorSelectAtLeastOneFile: 'Select at least one file to upload',
        uploadSuccess: 'Upload Successful',
        successUploadedFiles: 'Successfully uploaded {count} file(s){folderInfo}',
        createButton: 'Create',
        creatingButton: 'Creating...',
        uploadButton: 'Upload {count} file(s)',
        uploadSingleButton: 'Upload 1 file',
        clearSelectionButton: 'Clear selection',
        createFolderError: 'Enter a folder name',
        createFolderSuccess: 'Folder "{folderName}" created',
        documentsSectionTitle: 'Documents',
        documentsCount: '{count} documents in this knowledge base',
        documentsCountEmpty: 'Documents in this knowledge base',
        tooltipRefresh: 'Refresh',
        tooltipDownload: 'Download',
        tooltipDeleteFolder: 'Delete folder',
        tooltipDelete: 'Delete',
        tableHeaderName: 'Name',
        tableHeaderSize: 'Size',
        tableHeaderUpdated: 'Updated',
        tableHeaderActions: 'Actions',
        noFilesLoading: 'Loading files...',
        noFilesEmpty: 'No files in this knowledge base yet',
        loadMoreButton: 'Load more',
        sidebarAdmin: 'Admin',
        sidebarActionsTitle: 'Actions',
        sidebarBackTo: 'Back to {categoryLabel}',
        sidebarGoToAssessments: 'Go to Assessments',
        processingKnowledgebase: 'Processing Knowledgebase',
        estimatedTimeRemaining: 'Estimated time remaining: 10-20 minutes',
        reconfigureKbButton: 'Reconfigure Knowledge Base',
        reconfigureKbConfirm: 'Reconfigure this knowledge base? The AI will re-read the uploaded documents and rebuild its assessment framework. This usually takes 10-20 minutes and existing assessments are unaffected.',
        reconfigureKbStarted: 'Reconfiguring — this usually takes 10-20 minutes.',
        sidebarKBSummary: 'Knowledge Base Summary',
        sidebarProcessingTimeline: 'Processing Timeline',
        sidebarProcessingTimelineDescription: 'Knowledge base creation typically takes 20-30 minutes depending on the number of documents uploaded.',
        sidebarOrganisationWide: 'Organisation-Wide',
        sidebarOrganisationWideDescription: 'This knowledge base will be available for all assessments across your organisation.',
        sidebarSecureProcessing: 'Secure Processing',
        sidebarSecureProcessingDescription: 'All documents are encrypted and processed securely within your isolated environment.',
        // Category tab switcher
        tabGlobal: 'Global',
        tabProject: 'Project',
        tabProcurementActivity: 'Procurement Activity',
        tabFunding: 'Funding',
        // Stats row
        statsTotal: 'Total {categoryLabel}',
        statsActive: 'Active {categoryLabel}',
        statsDraft: 'Draft {categoryLabel}',
        statsRecentActivity: 'Recent Activity',
        statsThisWeek: 'this week',
        // Procurement filter tabs
        filterViewAll: 'View all',
        filterGoods: 'Goods',
        filterContractWorks: 'Contract Works',
        filterConsultancyServices: 'Consultancy Services',
        filterIndividualContractors: 'Individual Contractors',
        // Funding filter tabs
        filterFunds: 'Funds',
        filterGrants: 'Grants',
        filterScholarships: 'Scholarships',
        // View KB button
        viewKnowledgeBase: 'View Knowledge Base',
        // List page
        breadcrumb: 'Knowledge Bases',
        loadingKnowledgeBases: 'Loading knowledge bases...',
        confirmDelete: 'Are you sure you want to delete this knowledge base?',
        errorDeleteFailed: 'Failed to delete knowledge base',
        // Global KB page
        globalTitle: 'Global Knowledge Bases',
        globalCreateCard: 'Create New Global Knowledge Base',
        globalCategoryLabel: 'Global',
        globalSectionHeading: 'Organisation-wide knowledge',
        globalSectionSubheading: 'Create knowledge bases with policies, rules and templates that apply across all funding products.',
        // Project KB page
        projectTitle: 'Project Knowledge Bases',
        projectCreateCard: 'Create New Project Knowledge Base',
        projectCategoryLabel: 'Projects',
        projectSectionHeading: 'Key project information',
        projectSectionSubheading: 'Create knowledge bases for each project',
        projectEmptyState: 'No project knowledge bases available yet. Convert a personal knowledge base to shared and it will appear here.',
        // Procurement KB page
        procurementTitle: 'Procurement Activity Knowledge Bases',
        procurementCreateCard: 'Create New Procurement Activity Knowledge Base',
        procurementCategoryLabel: 'Activities',
        procurementSectionHeading: 'Procurement activities in play',
        procurementSectionSubheading: 'Create knowledge bases for each procurement activity you are working on.',
        // Funding KB page
        fundingTitle: 'Funds Knowledge Bases',
        fundingCreateCard: 'Create New Funding Knowledge Base',
        fundingCategoryLabel: 'Funding',
        fundingSectionHeading: 'Funding products',
        fundingSectionSubheading: 'Create knowledge bases for each fund, grant, or scholarship.',
        // Funding scope info
        scopeFundingTitle: 'Fund-Specific',
        scopeFundingDesc: 'This knowledge base is specific to this fund, grant, or scholarship',
        // Processing steps — dual labels (completed vs in-progress wording)
        stepUploadedCompleted: 'Documents uploaded to secure cloud storage',
        stepUploadedProcessing: 'Documents uploaded to secure cloud storage',
        stepProcessedCompleted: 'Documents processed and assessment rules extracted',
        stepProcessedProcessing: 'Processing documents to extract rules and policies',
        stepKbBuiltCompleted: 'Knowledge base built for application assessment',
        stepKbBuiltProcessing: 'Building knowledge base for application assessment',
        stepIndexedCompleted: 'Documents indexed and ready for quick reference',
        stepIndexedProcessing: 'Indexing documents for quick reference during assessments',
        // Progress & status labels
        progress: 'Progress',
        completed: 'Completed',
        errorLabel: 'Error',
        uploadErrorLabel: 'Upload Error',
        // Sidebar KB count labels
        sidebarSummaryTitle: 'Knowledge Bases',
        sidebarGlobalKBs: 'Global KBs',
        sidebarProjectKBs: 'Project KBs',
        sidebarProcurementKBs: 'Procurement Activity KBs',
        sidebarView: 'View',
        // Category labels (used in breadcrumbs, back links, detail pages)
        categoryGlobal: 'Global Knowledge Bases',
        categoryProject: 'Project Knowledge Bases',
        categoryProcurement: 'Procurement Activity Knowledge Bases',
        // Completed date
        completedDate: 'Completed:',
        // Info cards (scope + security)
        scopeOrgWideTitle: 'Organisation-Wide',
        scopeOrgWideDesc: 'This knowledge base will be available for areas of Nolia',
        scopeProjectTitle: 'Project-Specific',
        scopeProjectDesc: 'This knowledge base is specific to this project',
        scopeActivityTitle: 'Activity-Specific',
        scopeActivityDesc: 'This knowledge base is specific to this procurement activity',
        secureProcessingTitle: 'Secure Processing',
        secureProcessingDesc: 'All documents are processed and encrypted securely',
        // Documents uploaded section
        documentsUploadedTitle: 'Documents Uploaded',
        totalDocumentsLabel: 'Total Documents',
        noFilesInGroup: 'No files uploaded',
        removeFile: 'Remove',
        uploadLink: 'upload',
        // Group headings per category
        groupGlobalDocuments: 'Uploaded Knowledge Base Files',
        groupGlobalTemplates: 'Uploaded Output Template',
        groupProjectDocuments: 'Uploaded Knowledge Base Files',
        groupProcurementPreRfx: 'Uploaded Pre-RFx Documents Files',
        groupProcurementRfx: 'Uploaded RFx Documents Files',
        groupProcurementSupporting: 'Uploaded Supporting Documents Files',
        groupProcurementTemplates: 'Uploaded Output Template',
        groupFundingApplicationForm: 'Application Form',
        groupFundingSelectionCriteria: 'Assessment Criteria',
        groupFundingGoodExamples: 'Good Examples',
        groupFundingSupportingData: 'Supporting Data',
        groupFundingOutputTemplate: 'Output Template',
        // KB type tabs
        tabViewAll: 'View all',
        tabTermsOfReference: 'Terms of Reference',
        tabRequestForQuotation: 'Request for Quotation',
        tabExpressionOfInterest: 'Expression of Interest',
        tabRequestForBids: 'Request for Bids / Proposals',
        tabEvaluationReports: 'Evaluation Reports',
        // Wizard
        wizardGlobalTitle: 'Setup Global Knowledge Base',
        wizardFundingTitle: 'Setup Funding Knowledge Base',
        wizardProjectTitle: 'Setup Project Knowledge Base',
        wizardProcurementTitle: 'Setup Procurement Knowledge Base',
        wizardGlobalStepTitle: 'Global Documents',
        wizardGlobalStepDescription: 'Upload policies, rules, templates',
        wizardFundingApplicationFormDesc: 'Upload application form',
        wizardFundingSelectionCriteriaDesc: 'Upload assessment criteria',
        wizardFundingGoodExamplesDesc: 'Upload good examples',
        wizardFundingOutputTemplateDesc: 'Upload output template',
        wizardNameLabel: 'Start by naming your knowledge base.',
        wizardNamePlaceholderGlobal: 'e.g., [Company name] Global Knowledge base 2026',
        wizardNamePlaceholderFunding: 'e.g. Community Grants 2026',
        wizardNamePlaceholderProject: 'e.g. Project Atlas',
        wizardFileUploadLabelGlobal: 'Upload all relevant documentation for your global knowledge base — Policies, Rules, Templates, Standards, etc.',
        wizardClickToUpload: 'Click to upload',
        wizardClickToUploadAndAttach: 'and attach files',
        wizardOrDragAndDrop: 'or drag and drop',
        wizardPdfHint: 'PDF, DOC, DOCX, XLS, XLSX up to 100MB each',
        wizardPdfMdHint: 'PDF, DOC, DOCX, XLS, XLSX, MD up to 100MB each',
        wizardPdfWordTextHint: 'PDF, Word documents, or text files up to 100MB',
        wizardPdfWordExcelTextHint: 'PDF, Word, Excel documents, or Text files (max. 100MB each)',
        wizardSupportingDataHint: 'PDF, Word, Excel, CSV, TSV, JSON, TXT up to 50MB each',
        wizardBack: 'Back',
        wizardCreate: 'Create knowledge base',
        wizardCreating: 'Creating knowledge base...',
        wizardStepOf: 'Step {current} of {total}',
        wizardContinueTo: 'Continue to {step}',
        wizardDocumentsToUpload: 'Documents to upload',
        wizardProTip: 'Pro tip:',
        wizardKnowledgeBaseCreated: 'Knowledge Base Created!',
        wizardFinalizingKnowledgeBase: 'Finalizing Knowledge Base',
        wizardProgressLabel: 'Progress',
        wizardPleaseWait: 'Please wait while we set up the knowledge base...',
        wizardNameLabelFunding: 'Start by naming your funding KB *',
        wizardNamePlaceholderFundingNew: 'e.g., New to R&D',
        wizardSubtitleLabel: 'Add a subtitle (if needed)',
        wizardSubtitlePlaceholderGlobal: 'e.g., For all funding projects 2026',
        wizardSubtitlePlaceholderFunding: 'e.g. Callinnov-new2rd2025/26',
        wizardDescriptionLabel: 'Add brief description of this knowledge base',
        wizardDescriptionLabelFundingRequired: 'Add brief description of this knowledge base *',
        wizardDescriptionPlaceholderGlobal: 'e.g., Global Knowledge Base for all Funding Products 2025 — Policies, Rules, Templates, Standards.',
        wizardDescriptionPlaceholderFunding: 'e.g., Help businesses build R&D capability over up to two years, co-funding eligible costs.',
        wizardSelectFundingType: 'Select funding type *',
        wizardChooseOne: 'Choose one',
        wizardOptionFund: 'Fund',
        wizardOptionGrant: 'Grant',
        wizardOptionScholarship: 'Scholarship',
        // Sidebar info cards (funding variant)
        wizardSidebarOrgWideTitle: 'Organisation-Wide',
        wizardSidebarOrgWideDesc: 'These documents will apply to all Funding processes',
        wizardSidebarStandardsTitle: 'Standards & Compliance',
        wizardSidebarStandardsDesc: 'Ensure all activities meet organisational standards',
        wizardSidebarGlobalKnowledgeTitle: 'Global Knowledge',
        wizardSidebarGlobalKnowledgeDesc: 'Documents will be used as reference across all assessments',
        wizardSidebarFundSpecificTitle: 'Fund-Specific',
        wizardSidebarFundSpecificDesc: 'These documents are unique to this fund and will guide the assessment process',
        wizardSidebarQualityTitle: 'Quality Standards',
        wizardSidebarQualityDesc: 'Upload high-quality examples to help the AI learn what good applications look like',
        wizardSidebarAITitle: 'AI-Powered Analysis',
        wizardSidebarAIDesc: 'Documents will be analyzed to create a tailored assessment framework',
        // Funding wizard step labels (specific to FUNDING_WIZARD_CONFIG)
        wizardFundingApplicationFormTitle: 'Upload Form',
        wizardFundingApplicationFormDescription: 'Name fund & upload application form',
        wizardFundingApplicationFormUploadLabel: "Upload your current application form. We'll analyse the structure and add this to the system.",
        wizardFundingSelectionCriteriaTitle: 'Selection Criteria',
        wizardFundingSelectionCriteriaDescription: 'Upload assessment criteria',
        wizardFundingSelectionCriteriaUploadLabel: 'Upload your assessment criteria documents. These will help Nolia validate applications and ensure only qualified submissions are processed. Upload multiple documents like scoring rubrics, evaluation criteria, and assessment guidelines. The more context you provide, the better the AI will understand your requirements.',
        wizardFundingGoodExamplesTitle: 'Good Examples',
        wizardFundingGoodExamplesDescription: 'Upload high-quality examples',
        wizardFundingGoodExamplesUploadLabel: 'Upload examples of high-quality applications that meet your standards. The AI will learn from these to set quality expectations and guide applicants. Upload your best applications — those that scored highly and met all criteria. Nolia will learn from these to help future applicants create better submissions and reduce your assessment workload.',
        wizardFundingSupportingDataTitle: 'Supporting Data',
        wizardFundingSupportingDataDescription: 'Lookup files the AI can reference',
        wizardFundingSupportingDataUploadLabel: 'Upload reference datasets the assessor should consult — school directories, approved-provider lists, prior recipients, course lists, etc. Each file needs a short description of what it contains and when to use it.',
        wizardFundingOutputTemplateTitle: 'Output Template',
        wizardFundingOutputTemplateDescription: 'Define output format',
        wizardFundingOutputTemplateUploadLabel: 'Upload an output template that defines how the assessments should be formatted and presented.',
        // Funding-global step
        wizardFundingGlobalStepTitle: 'Global Documents',
        wizardFundingGlobalStepDescription: 'Upload policies, rules, templates',
        wizardFundingGlobalStepUploadLabel: 'Upload all relevant documentation for your global knowledge base — Policies, Rules, Templates, Standards, etc.',
        // Wizard page-level title
        wizardFundingGlobalConfigTitle: 'Setup Global Knowledge Base',
        wizardFundingConfigTitle: 'Setup New Fund',
        wizardFundingSubtitle: 'Knowledge Bases',
        // KB card labels
        cardGlobalKb: 'Global knowledge base',
        cardFundingKb: 'Funding knowledge base',
        cardProjectKb: 'Project knowledge base',
        cardProcurementKb: 'Procurement knowledge base',
        cardSharedAccess: 'Shared access',
        cardPersonalAccess: 'Personal access',
        cardDocumentCount: '{count} document',
        cardDocumentCountPlural: '{count} documents',
    },

    nolia: {
        pageTitle: 'Nolia Analysis',
        selectKBSection: '1. Select Knowledge Bases *',
        selectKBUpdating: 'Updating...',
        selectKBDescription: 'Select a global knowledge base and a funding knowledge base for your analysis.',
        uploadDocumentSection: '2. Upload Document *',
        uploadDocumentTitle: 'Upload Document',
        uploadDocumentDescription: 'Upload any document for analysis (PDF, DOC, DOCX, TXT, XLSX, XLS, CSV)',
        selectOutputLanguageSection: '3. Select Output Language',
        selectOutputLanguageDescription: 'Choose the language for the final report. Documents in other languages will be translated during processing.',
        uploadedFileLabel: 'Uploaded file',
        removeDocument: 'Remove document',
        replaceButton: 'Replace',
        chooseFileButton: 'Choose file',
        fileSize: '{size} MB • {type}',
        fileTypeUnknown: 'Unknown type',
        analysisProgressTitle: 'Analysis Progress',
        analysisCompleteTitle: 'Analysis Complete',
        analysisCompleteHideResults: 'Hide Results',
        analysisCompleteSummary: 'Analysis completed',
        analysisCompleteViewResults: 'View Results',
        aboutTitle: 'About Nolia Analysis',
        aboutPoint1: 'Nolia uses advanced AI to analyze documents against specialized knowledge bases',
        aboutPoint2: 'Analysis includes content extraction, pattern recognition, and intelligent insights',
        aboutPoint3: 'Results provide comprehensive analysis with contextual recommendations',
        aboutPoint4: 'Different knowledge bases offer domain-specific expertise for specialized analysis',
        startAnalysisButton: 'Start Nolia Analysis',
        processingAnalysis: 'Processing Analysis...',
        errorDocumentRequired: 'Document is required',
        errorSelectBothKBs: 'Please select both a global and funding knowledge base',
        errorUploadFailed: 'Upload failed: {error}',
        errorAnalysisFailed: 'Analysis failed: {error}',
        uploadDocumentHint: 'Please upload a document to proceed.',
        jobIdLabel: 'Job ID:',
        statusLabel: 'Status:',
    },

    procurement: {
        // Overview (Page 1)
        overviewSubtitle: 'Procurement Activities',
        overviewTitle: 'Procurement Activity Overview',

        loadingActivities: 'Loading procurement activities...',
        emptyState: 'No procurement activities found.',
        tabViewAll: 'View all',
        tabGoods: 'Goods',
        tabContractWorks: 'Contract Works',
        tabConsultancyServices: 'Consultancy Services',
        tabIndividualContractors: 'Individual Contractors',

        // Activity Detail (Page 2)
        activitySubtitle: 'Procurement Activity',
        technicalResponses: 'Technical Responses',
        technical: 'Technical',
        combined: 'Combined',
        uploadResponse: 'Upload Response',
        actions: 'Actions',
        tableResponseFromCol: 'Response from',
        tableSubmittedCol: 'Submitted',
        tableStatusCol: 'Status',
        tableScoreCol: 'Score',
        statusComplete: 'Complete',

        // Upload (Page 5)
        uploadResponseTitle: 'Upload Response',
        uploadResponseSubtitle: 'Procurement Activities',
        stepUploadResponse: 'Upload',
        stepUploadResponseDesc: 'Upload response to evaluate',
        stepEvaluation: 'Evaluation',
        stepEvaluationDesc: 'Automated AI response evaluation',

        // Individual Assessment (Page 3)
        individualEvalSubtitle: 'Individual Bid Evaluation',
        evaluationComplete: 'Evaluation Complete',
        evaluationCompleteDesc: 'AI-powered evaluation of bid response against procurement requirements.',
        pointsLabel: 'points',

        // Comparative Analysis (Page 4)
        comparativeSubtitle: 'Comparative Evaluation Analysis',
        comparativeAnalysisLabel: 'Three-Bidder Competitive Analysis',
        biddersCompared: 'Bidders Compared',
        totalProjectValue: 'Total Project Value',
        allQualified: 'All Qualified',
        onBudget: 'On Budget',
        analysisComplete: 'Analysis Complete',
        analysisCompleteDesc: 'AI-powered comparative analysis of all qualified bids ready for award decision.',

        // Quick Summary sidebar
        quickSummary: 'Quick Summary',
        financialMargin: 'Financial Margin',
        costPerUnit: 'Cost/Unit',
        strategicAdvantage: 'Strategic Advantage',
        viewEvaluation: 'View evaluation',
        technicalScore: 'Technical Score',
    },

    funding: {
        // Overview
        overviewSubtitle: 'Assess Applications',
        overviewTitle: 'Funding Products Overview',
        loadingActivities: 'Loading funding products...',
        emptyState: 'No funding products found.',

        // Activity Detail
        activitySubtitle: 'Assess Applications',
        responses: 'Responses',
        uploadApplication: 'Upload Application',
        actions: 'Actions',
        compareApplications: 'Compare Applications',
        tableResponseFromCol: 'Response from',
        tableSubmittedCol: 'Submitted',
        tableStatusCol: 'Status',
        tableScoreCol: 'Score',
        statusComplete: 'Complete',

        // Upload
        uploadTitle: 'Upload Response',
        uploadSubtitle: 'Assess Applications',
        selectGlobalKBLabel: 'Select the Global Knowledge Base to assess against',
        selectFundingKBLabel: 'Select the Fund to assess against',
        uploadDocsLabel: 'Upload document(s) to assess.',

        // Individual Assessment
        individualSubtitle: 'Individual Application Assessment',
        overallAssessment: 'Overall Assessment',
        fundingAmount: 'Funding Amount',
        assessmentComplete: 'Assessment Complete',
        assessmentCompleteDesc: 'AI-powered assessment of grant application against funding criteria.',
        downloadPdf: 'Download as PDF',
        downloadWord: 'Download as Word',

        // Compare
        compareTitle: 'Application Comparison Analysis',
        downloadComparisonReport: 'Download comparison report',

        // User management terminology
        assignFund: 'Assign Fund',
        fundingProductsCount: '{count} funds',
        fundingProductsPopoverTitle: 'Funding Products',
        fundingProductsPopoverDesc: 'Which Funds/Grants/Scholarships do you want them to see',
        fundingProductsAssignedDesc: 'The Funds/Grants/Scholarships assigned to this user',
        fundingProductsLabel: 'Funding Products',
        fundingProductsDescription: 'Assign which Funds/Grants/Scholarships to see',

        // Dashboard feature cards
        featureAssessTitle: 'Assess Applications',
        featureAssessDescription: 'The ability to assess fund, grant and scholarship applications at speed.',
        featureAssessAction: 'Assess',
        featureAnalyticsTitle: 'Analytics & Insights',
        featureAnalyticsDescription: 'Detailed analytics and insights across your funding products and assessments.',
        featureAnalyticsAction: 'View',

        // Activity table column headers + helpers
        applicantCol: 'Applicant',
        submittedByCol: 'Submitted by',
        submittedCol: 'Submitted',
        statusCol: 'Status',
        decisionCol: 'Decision',
        scoreCol: 'Score',
        costStagingCol: 'Cost (staging)',
        actionsHidden: 'Actions',
        responsesAriaLabel: 'Application responses',
        responsesWithCount: 'Responses ({count})',
        scoreNa: 'N/A',
        applicantIdBadge: 'Applicant ID',
        applicantIdLabel: 'Applicant ID',
        applicantIdOptionalLabel: 'Applicant ID (optional)',
        applicantIdPlaceholder: 'e.g. APP-2026-04 — leave blank if you don\'t have one',
        applicantIdHint: 'Used to group repeat assessments of the same applicant and warn you if you\'ve assessed them before. If left blank, we\'ll attempt to extract one from the application — but only if a real identifier exists in the documents. Without an ID, this assessment won\'t be grouped.',
        applicantIdDuplicateWarningOne: 'This applicant ID has 1 prior assessment ({date}). Submitting will create a new version.',
        applicantIdDuplicateWarningMany: 'This applicant ID has {count} prior assessments (most recent: {date}). Submitting will create a new version.',
        applicantIdDuplicateView: 'View latest',
        fileUploadHint: 'PDF, Word, Excel, images, or HTML up to 50MB each',
        compareApplicationsCount: 'Compare Applications ({count})',
        startingComparison: 'Starting comparison…',
        compareSelectionHint: 'Select between {min} and {max} applications to enable comparison.',
        selectionWarningMax: 'Maximum {max} selected. Uncheck one to swap it out.',
        selectionWarningCap: 'You can compare a maximum of {max} applications at a time.',
        loadingApplications: 'Loading applications…',
        noApplicationsYet: 'No applications yet',
        noApplicationsHint: 'Click "Upload Application" to run the first one',
        extractingApplicantDetails: 'Extracting applicant details — this appears as soon as the extract phase finishes.',
        noApplicantDetails: 'No applicant details extracted.',
        deleteAssessmentTitle: 'Delete assessment?',
        deleteAssessmentDescription: 'This will permanently remove the assessment and its generated report. This cannot be undone.',
        deleteConfirm: 'Delete',
        deleteCancel: 'Cancel',
        deleteForbiddenTitle: 'You can’t delete this',
        deleteAssessmentForbidden: 'This assessment was created by another user. Only the user who ran an assessment can delete it.',
        deleteComparisonForbidden: 'This comparison was created by another user. Only the user who ran a comparison can delete it.',
        deleteForbiddenAck: 'OK',
        toggleHideApplicant: 'Hide applicant details',
        toggleShowApplicant: 'Show applicant details',
        expandPlaceholderProcessing: 'Assessment in progress — applicant details and recommendation will appear here once the run completes.',
        expandPlaceholderProcessingWithVersions: 'Assessment in progress — applicant details and recommendation will appear here once the run completes. Use the version switcher above to view a previous assessment in the meantime.',
        expandPlaceholderNoData: 'No applicant details or recommendation were extracted for this run.',
        viewAssessment: 'View assessment',
        openInProgressAssessment: 'Open in-progress assessment',
        deleteAssessment: 'Delete assessment',
        versionLatest: 'Latest',

        // Status labels (statusComplete defined above)
        statusProcessing: 'Processing',
        statusQueued: 'Queued',
        statusFailed: 'Failed',
        statusNeedsReview: 'Needs Review',

        // Errors / fallbacks
        errorLoadAssessments: 'Failed to load assessments',
        errorLoadComparison: 'Failed to load comparison',
        errorStartComparison: 'Failed to start comparison',
        errorDeleteAssessment: 'Failed to delete assessment',
        errorDownload: 'Failed to download {ext}',
        errorGenerateDownload: 'Failed to generate download URL',
        errorLoadAssessmentContent: 'Failed to load assessment content',
        errorParseComparisonData: 'Failed to parse comparison data',
        errorMissingSubmitter: 'One or more selected runs is missing a submitter — try reloading.',
        markdownNotAvailable: 'Markdown source not available for this assessment.',
        markdownNotLoaded: 'Cannot convert — assessment markdown is not loaded yet.',
        applicantFallback: 'Applicant',
        fundFallback: 'Fund',
        fundingProductFallback: 'Funding Product',

        // Assessment view
        decisionTitle: 'Decision',
        nextActionLabel: 'Next action',
        actionByLabel: 'Action by',
        recommendedAmountLabel: 'Recommended amount',
        blockingReasonsLabel: 'Blocking reasons',
        needsReviewTitle: 'Needs Review',
        needsReviewDescription: 'The pipeline flagged this assessment for human follow-up. Check the details below before finalising.',
        assessmentInProgress: 'Assessment in progress…',
        assessmentDefaultMessage: 'Starting pipeline — extracting applicant info, evaluating against criteria, generating the report.',
        assessmentFailed: 'Assessment failed',
        loadingAssessmentReport: 'Loading assessment report…',
        preparingPdf: 'Preparing PDF…',
        preparingDocx: 'Preparing DOCX…',
        preparingDownload: 'Preparing…',
        downloadDocx: 'Download as DOCX',
        downloadMarkdown: 'Download Markdown',
        download: 'Download',
        noAssessmentSelected: 'No assessment selected.',

        // Comparison view
        comparisonInProgress: 'Comparison in progress…',
        comparisonDefaultMessage: 'Reading prior assessments and building the side-by-side view.',
        comparisonTimeEstimate: 'This may take a couple of minutes.',
        comparisonFailed: 'Comparison failed',
        comparisonFallback: 'Comparison',
        sideBySideUnavailable: 'Side-by-side view unavailable',
        sideBySideUnavailableDesc: "We couldn't parse the comparison JSON for this run. Showing the Markdown report instead — the downloads below are unaffected.",
        noComparisonSelected: 'No comparison selected.',

        // Boolean rendering
        applicantValueYes: 'Yes',
        applicantValueNo: 'No',

        // Comparison side-by-side
        comparisonFundingFallback: 'Funding',
        comparisonGenerated: 'Generated {timestamp}',
        comparisonCriterionHeader: 'Criterion',
        comparisonNarrativeHeading: 'Narrative',
        comparisonNoData: 'No data',
        comparisonGlobalRule: 'Global rule',
        comparisonFundSpecificRule: 'Fund-specific rule',

        // Status badge — single-step pipeline rename: needs_review → inputs_unreliable.
        // statusNeedsReview is left in place above for any consumers still on the old shape.
        statusVerifyInputs: 'Verify Inputs',

        // Submitter sub-line in the Applicant cell (folded out of its own column).
        submittedByPrefix: 'Submitted by {name}',

        // Inline mirror chips inside the Applicant cell at narrow widths
        // (when the corresponding column is hidden).
        mirrorSubmittedLabel: 'Submitted',
        mirrorScoreLabel: 'Score',
        mirrorCostLabel: 'Cost',

        // Activity table — extra empty / loading copy on the comparisons tab.
        loadingComparisons: 'Loading comparisons…',
        noComparisonsYet: 'No comparisons yet',
        noComparisonsHint: 'Switch to Applications, select 2–3 rows, and run a comparison',
        applicantsCol: 'Applicants',

        // Confirm-delete dialog branches.
        deleteComparisonTitle: 'Delete comparison?',
        deleteComparisonDescription: 'This will permanently remove the comparison and its generated report. This cannot be undone.',
        deleteComparison: 'Delete comparison',
        viewComparison: 'View comparison',
        openInProgressComparison: 'Open in-progress comparison',

        // Recommendation panel inside the row expander.
        recommendationLabel: 'Recommendation',
        viewAiSummary: 'View AI summary →',

        // SummaryDialog (modal opened from the row expander pill).
        summaryDialogTitle: 'AI summary & reasoning',
        summaryDialogSubtitle: 'A pipeline-generated overview of the assessment — not the formal assessment document.',
        summaryDialogClose: 'Close',
        summaryDialogEmpty: 'No summary available for this run.',

        // AccordionSection labels on the assessment page.
        sectionRecommendationTitle: 'Pipeline recommendation',
        sectionRecommendationEyebrow: 'AI-generated. Final decision sits with the assessor.',
        sectionSummaryTitle: 'AI summary & reasoning',
        sectionSummaryEyebrow: 'Pipeline-generated overview — not the formal assessment document.',
        sectionDocumentTitle: 'Assessment document',
        sectionDocumentEyebrow: 'The formal report following your output template.',
        recommendedPrefix: 'Recommended: {amount}',

        // Verify Inputs banner on the assessment page (renames Tony's needsReviewTitle/Description).
        verifyInputsBannerTitle: 'Verify Inputs',
        verifyInputsBannerDescription: "The uploaded files don't appear to be a complete funding application for the named applicant. Verify the documents and applicant identity before relying on this assessment.",

        // Summary fetch error on the assessment page.
        summaryFetchError: "Couldn't load the AI summary",
    },

    admin: {
        globalPageTitle: 'Nolia | Global Knowledge Bases',
        globalHeading: 'Global Knowledge Bases',
        globalSectionHeading: 'Organisation-wide knowledge',
        globalSectionSubheading: 'Create knowledge bases with policies, rules and templates that apply across all funding products.',
        globalCategoryAll: 'View all',
        globalCategoryTermsOfReference: 'Terms of Reference',
        globalCategoryRFQ: 'Request for Quotation',
        globalCategoryEOI: 'Expression of Interest',
        globalCategoryRFP: 'Request for Bids / Proposals',
        globalCategoryEvaluation: 'Evaluation Reports',
        globalCreateKBLabel: 'Global KB name',
        globalCreateKBPlaceholder: 'e.g. Contracts Playbook',
        globalCreateKBButton: 'Create',
        globalCreateKBButtonLoading: 'Creating…',
        globalCancelButton: 'Cancel',
        globalCreateNewKB: 'Create new knowledge base',
        globalLoadingText: 'Loading knowledge bases...',
        globalErrorDefaultMsg: 'Knowledge base name is required.',
        procurementPageTitle: 'Nolia | Procurement Activity Knowledge Bases',
        procurementHeading: 'Procurement Activity Knowledge Bases',
        procurementSectionHeading: 'Procurement activities in play',
        procurementSectionSubheading: "Create knowledge bases for each procurement activity you're working on.",
        procurementCreateKBLabel: 'Procurement KB name',
        procurementCreateKBPlaceholder: 'e.g. MRI Procurement',
        procurementCreateKBButton: 'Create',
        procurementCreateKBButtonLoading: 'Creating…',
        procurementCancelButton: 'Cancel',
        procurementCreateNewKB: 'Create new knowledge base',
        procurementKbSubtitle: 'Procurement knowledge base',
        procurementDocumentCount: 'documents',
        procurementRoleLine: 'Role: {role}',
        procurementCreatedDate: 'Created {date}',
        procurementErrorDefaultMsg: 'Knowledge base name is required.',
        projectPageTitle: 'Nolia | Project Knowledge Bases',
        projectHeading: 'Project Knowledge Bases',
        projectSectionHeading: 'Key project information',
        projectSectionSubheading: 'Create knowledge bases for each project',
        projectCreateKBLabel: 'Project KB name',
        projectCreateKBPlaceholder: 'e.g. Project Atlas',
        projectCollaboratorsLabel: 'Collaborators (optional)',
        projectCollaboratorsPlaceholder: 'alice@example.com, bob@example.com',
        projectCollaboratorsHelpText: 'Separate multiple emails or user IDs with commas. Everyone still gets access.',
        projectCreateKBButton: 'Create',
        projectCreateKBButtonLoading: 'Creating…',
        projectCancelButton: 'Cancel',
        projectCreateNewKB: 'Create new knowledge base',
        projectEmptyState: 'No project knowledge bases available yet. Convert a personal knowledge base to shared and it will appear here.',
        projectStatusComplete: 'Complete',
        projectStatusProcessing: 'Processing',
        projectStatusFailed: 'Failed',
        projectErrorDefaultMsg: 'Knowledge base name is required.',
    },
    secure: {
        documentLabel: 'Security & Compliance Posture',
        company: 'Nolia',
        product: 'Nolia',
        title: 'Security & Compliance Posture',
        subtitle: "A public declaration of Nolia's security commitments, certifications, and assurance activities.",
        effectiveDate: 'Effective date',
        lastUpdated: 'Last updated',
        documentVersion: 'Document version',
        version: '1.0',
        date: '29 April 2026',
        lastUpdatedDate: '30 April 2026',
        sections: {
            overview: {
                title: '1. Overview',
                body: 'Nolia is an enterprise procurement and funding compliance platform. This document is a public statement of the security and compliance posture of the Nolia platform. It is intended for prospective customers, existing customers, partners, and auditors who require an at-a-glance summary of the controls, attestations, and assurance activities that govern the operation of Nolia. Specific evidence (reports, attestations, datasheets) is available either via the linked datasheets below or under appropriate non-disclosure terms on request.',
            },
            scope: {
                title: '2. Scope',
                body: 'Unless explicitly stated otherwise, the statements in this document apply to the production Nolia SaaS platform, including all multi-tenant components and per-tenant infrastructure operated by Nolia on Amazon Web Services. Where a control or attestation applies only to a subset of the platform, the relevant datasheet linked from this page describes the precise scope.',
            },
            certifications: {
                title: '3. Certifications & Attestations',
                body: 'The following certifications and third-party attestations are currently in scope for the Nolia platform. Each entry links to a public datasheet describing the scope, period of coverage, and how to request the underlying report.',
                tableHeaders: {
                    framework: 'Framework',
                    status: 'Status',
                    scope: 'Scope',
                    details: 'Details',
                },
                items: {
                    soc2: {
                        framework: 'SOC 2',
                        status: 'In scope',
                        scope: 'Nolia SaaS platform',
                        linkLabel: 'View datasheet',
                        ndaOnly: 'Available under NDA',
                    },
                },
            },
            penTesting: {
                title: '4. Penetration Testing',
                body: 'Nolia is subject to penetration testing on a recurring cadence. Summaries of recent engagements are listed below. Detailed reports, including findings and remediation plans, are available to qualified parties under NDA.',
                tableHeaders: {
                    date: 'Date',
                    type: 'Type',
                    scope: 'Scope',
                    status: 'Report',
                    remediation: 'Remediation',
                },
                engagements: [
                    {
                        date: '30 Apr 2026',
                        type: 'Internal',
                        scope: 'Nolia web application and API',
                        status: 'Under NDA',
                        remediation: 'Complete — 46/46 findings closed (May 2026)',
                    },
                    {
                        date: '20–22 Jan 2026',
                        type: 'Third-party',
                        scope: 'Nolia web application and API — Bastion Security Group, engagement BSG-ARC-13394',
                        status: 'Under NDA',
                        remediation: 'Complete — 10/10 findings closed (May 2026); retest pending',
                    },
                ],
                disclaimer: 'Specific findings, exploitation evidence, and remediation status are not published on this page. Please contact us using the details below to request the detailed report under NDA.',
            },
            dataProtection: {
                title: '5. Data Protection',
                body: 'Customer data is encrypted in transit using TLS 1.2 or higher and at rest using AWS-managed encryption keys. Each customer is provisioned into an isolated AWS account, providing strong tenant separation at the infrastructure layer. Access to customer environments by Nolia personnel is logged and limited to authenticated, role-based assumed-role access.',
            },
            responsibleDisclosure: {
                title: '6. Responsible Disclosure',
                body: 'We welcome reports from independent security researchers. If you believe you have identified a security vulnerability in Nolia, please report it to the address below. We commit to acknowledging valid reports promptly and will work in good faith with reporters who follow responsible-disclosure practices.',
            },
            contact: {
                title: '7. Security Contact',
                body: 'For security inquiries, vulnerability reports, or to request copies of the underlying reports referenced on this page, please contact:',
                email: 'security@getnolia.io',
            },
        },
        footer: {
            disclaimer: 'This document is provided for informational purposes only and does not modify any contractual rights or obligations between Nolia and its customers. The contents are subject to change without notice.',
            copyright: '© {year} Nolia. All rights reserved.',
        },
    },
};

export type TranslationDict = typeof en;
